import { useState } from "react";

import { Button } from "@/components/ui/button.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { RULE_FIELD_LABEL, RULE_WORDS_HINT } from "@/shared/taskRowCopy.ts";
import { useAddTaskRule, useDraftTaskRule } from "./api.ts";

/** The longest rule the server keeps. */
const MAX_RULE_CHARS = 300;

/**
 * "Create a rule for this": the user says it in their own words, okthx
 * drafts the rule from them and the task, and the user confirms or edits it.
 * Nothing is saved before Save rule. A rule judges new tasks only; the list as
 * it stands is not changed.
 */
export const TaskRulePanel = ({
  taskId,
  onSaved,
  onCancel,
}: {
  taskId: string;
  /** The rule is saved; the row answers the task itself. */
  onSaved: () => void;
  onCancel: () => void;
}) => {
  const draft = useDraftTaskRule();
  // From the mutation, not `mutate`: Cancel can close the panel while it saves.
  const save = useAddTaskRule(onSaved);
  const [words, setWords] = useState("");
  const [rule, setRule] = useState<string | null>(null);

  return (
    <div className="flex w-full flex-col gap-2">
      {rule === null ? (
        <Textarea
          aria-label={RULE_FIELD_LABEL}
          placeholder={RULE_WORDS_HINT}
          className="min-h-16 text-sm"
          maxLength={500}
          value={words}
          onChange={(e) => setWords(e.target.value)}
        />
      ) : (
        <Textarea
          aria-label="The rule"
          className="min-h-16 text-sm"
          maxLength={MAX_RULE_CHARS}
          value={rule}
          onChange={(e) => setRule(e.target.value)}
        />
      )}
      <div className="flex gap-2">
        {rule === null ? (
          <Button
            type="button"
            size="sm"
            className="h-7 px-2 text-xs"
            data-statsig-name="tasks-rule-draft"
            disabled={draft.isPending}
            onClick={() =>
              draft.mutate(
                { taskId, ...(words.trim() ? { words: words.trim() } : {}) },
                { onSuccess: ({ text }) => setRule(text) },
              )
            }
          >
            Draft rule
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="h-7 px-2 text-xs"
            data-statsig-name="tasks-rule-save"
            disabled={save.isPending || rule.trim().length < 3}
            onClick={() => save.mutate({ text: rule.trim(), fromTaskId: taskId })}
          >
            Save rule
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          data-statsig-name="tasks-rule-cancel"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
